import { NodeViewWrapper } from '@tiptap/react'
import { useRef, useCallback, useState } from 'react'
import { AlignLeft, AlignCenter, AlignRight, Move } from 'lucide-react'

export const IMAGE_SIZES = [
  { label: 'Thumb',  value: '80px',  title: 'Thumbnail — 80px'       },
  { label: 'XS',     value: '120px', title: 'X-Small — 120px'        },
  { label: 'Small',  value: '200px', title: 'Small — 200px'          },
  { label: 'Medium', value: '320px', title: 'Medium — 320px'         },
  { label: 'Large',  value: '480px', title: 'Large — 480px'          },
  { label: 'XL',     value: '640px', title: 'X-Large — 640px'        },
  { label: '2XL',    value: '800px', title: '2X Large — 800px'       },
  { label: '25%',    value: '25%',   title: 'Quarter Width — 25%'    },
  { label: '50%',    value: '50%',   title: 'Half Width — 50%'       },
  { label: 'Full',   value: '100%',  title: 'Full Width — 100%'      },
]

// Parse width and float-based alignment out of the style string
function parseStyle(style) {
  if (!style) return { width: null, align: 'none' }
  const wm = style.match(/(?:^|;)\s*width\s*:\s*([^;]+)/i)
  const fm = style.match(/(?:^|;)\s*float\s*:\s*([^;]+)/i)
  const width = wm ? wm[1].trim() : null
  const fv    = fm ? fm[1].trim() : null
  const align = fv === 'left' ? 'left' : fv === 'right' ? 'right' : 'none'
  return { width, align }
}

export function makeImageStyle(width, align) {
  let base = `width: ${width}; max-width: 100%; height: auto;`
  if (align === 'left')  return `float: left; margin: 0 1rem 0.5rem 0; ${base}`
  if (align === 'right') return `float: right; margin: 0 0 0.5rem 1rem; ${base}`
  return base
}

export default function ResizableImageComponent({ node, updateAttributes, selected }) {
  const imageRef   = useRef(null)
  const startX     = useRef(0)
  const startWidth = useRef(0)

  const [dragging,    setDragging]    = useState(false)
  const [dragWidth,   setDragWidth]   = useState(null)
  const [customSize,  setCustomSize]  = useState('')
  const [showCustom,  setShowCustom]  = useState(false)

  const { width: styleWidth, align } = parseStyle(node.attrs.style)
  const displayWidth = dragWidth ? `${dragWidth}px` : (styleWidth || 'auto')

  const applySize = useCallback((newWidth) => {
    updateAttributes({ style: makeImageStyle(newWidth, align) })
    setShowCustom(false)
    setCustomSize('')
  }, [updateAttributes, align])

  const applyAlign = useCallback((newAlign) => {
    updateAttributes({ style: makeImageStyle(styleWidth || '100%', newAlign) })
  }, [updateAttributes, styleWidth])

  const applyCustom = useCallback(() => {
    const v = customSize.trim()
    if (!v) return
    applySize(/^\d+$/.test(v) ? `${v}px` : v)
  }, [customSize, applySize])

  const onDragMouseDown = useCallback((e) => {
    e.preventDefault()
    startX.current     = e.clientX
    startWidth.current = imageRef.current?.offsetWidth || 300

    const getMax = () =>
      imageRef.current?.closest('.ProseMirror')?.offsetWidth ||
      imageRef.current?.parentElement?.offsetWidth ||
      800

    const onMove = (mv) => {
      const w = Math.max(80, Math.min(startWidth.current + mv.clientX - startX.current, getMax()))
      setDragging(true)
      setDragWidth(Math.round(w))
    }

    const onUp = (up) => {
      const w = Math.max(80, Math.min(startWidth.current + up.clientX - startX.current, getMax()))
      updateAttributes({ style: makeImageStyle(`${Math.round(w)}px`, align) })
      setDragging(false)
      setDragWidth(null)
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [updateAttributes, align])

  return (
    <NodeViewWrapper
      style={{
        display:       'inline-block',
        position:      'relative',
        width:         displayWidth,
        maxWidth:      '100%',
        userSelect:    'none',
        float:         align !== 'none' ? align : undefined,
        margin:        align === 'right' ? '0 0 0.5rem 1rem'
                     : align === 'left'  ? '0 1rem 0.5rem 0'
                     : '0.5rem 0',
        outline:       selected ? '2px solid #A67C52' : 'none',
        outlineOffset: '2px',
        verticalAlign: 'middle',
      }}
    >
      <img
        ref={imageRef}
        src={node.attrs.src}
        alt={node.attrs.alt || ''}
        title={node.attrs.title || ''}
        style={{ width: '100%', display: 'block', borderRadius: '0.5rem' }}
        draggable={false}
      />

      {/* Drag width tooltip */}
      {dragging && dragWidth && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          background: 'rgba(0,0,0,0.75)', color: '#fff',
          fontSize: 12, padding: '3px 8px', borderRadius: 4,
          pointerEvents: 'none', zIndex: 30, whiteSpace: 'nowrap',
        }}>
          {dragWidth}px
        </div>
      )}

      {/* Right-edge drag-resize handle */}
      <div
        onMouseDown={onDragMouseDown}
        style={{
          position: 'absolute', right: -4, top: '50%',
          transform: 'translateY(-50%)',
          width: 8, height: 48, background: '#A67C52', borderRadius: 4,
          cursor: 'col-resize', opacity: 0, transition: 'opacity 0.15s', zIndex: 10,
        }}
        onMouseEnter={e => { e.currentTarget.style.opacity = '1' }}
        onMouseLeave={e => { if (!dragging) e.currentTarget.style.opacity = '0' }}
      />

      {/* ── Floating toolbar: shown only when the image node is selected ── */}
      {selected && !dragging && (
        <>
          {/* ── Top bar: size presets ── */}
          <div
            onMouseDown={e => e.stopPropagation()}
            style={{
              position: 'absolute', top: 0, left: 0, right: 0,
              background: 'rgba(0,0,0,0.80)',
              padding: '5px 6px 4px',
              zIndex: 20,
              borderRadius: '0.5rem 0.5rem 0 0',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            {/* Preset size buttons */}
            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {IMAGE_SIZES.map(({ label, value, title }) => {
                const active = styleWidth === value
                return (
                  <button
                    key={value}
                    type="button"
                    title={title}
                    onClick={(e) => { e.stopPropagation(); applySize(value) }}
                    style={{
                      padding: '2px 5px',
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.3,
                      borderRadius: 3,
                      border: 'none',
                      background: active ? '#A67C52' : 'rgba(255,255,255,0.14)',
                      color: '#fff',
                      cursor: 'pointer',
                      lineHeight: 1.6,
                    }}
                  >
                    {label}
                  </button>
                )
              })}

              {/* Custom toggle */}
              <button
                type="button"
                title="Custom size (px or %)"
                onClick={(e) => { e.stopPropagation(); setShowCustom(v => !v) }}
                style={{
                  padding: '2px 5px', fontSize: 10, fontWeight: 700,
                  letterSpacing: 0.3, borderRadius: 3, border: 'none',
                  background: showCustom ? '#A67C52' : 'rgba(255,255,255,0.14)',
                  color: '#fff', cursor: 'pointer', lineHeight: 1.6,
                }}
              >
                Custom
              </button>
            </div>

            {/* Custom size input row */}
            {showCustom && (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="e.g. 400px or 60%"
                  value={customSize}
                  onChange={e => setCustomSize(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') applyCustom() }}
                  onClick={e => e.stopPropagation()}
                  autoFocus
                  style={{
                    flex: 1, fontSize: 11, padding: '2px 6px',
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.25)',
                    background: 'rgba(255,255,255,0.1)',
                    color: '#fff', outline: 'none',
                  }}
                />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); applyCustom() }}
                  style={{
                    padding: '2px 8px', fontSize: 11, borderRadius: 3,
                    border: 'none', background: '#A67C52',
                    color: '#fff', cursor: 'pointer',
                  }}
                >
                  Apply
                </button>
              </div>
            )}
          </div>

          {/* ── Bottom bar: alignment + current size label ── */}
          <div
            onMouseDown={e => e.stopPropagation()}
            style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: 'rgba(0,0,0,0.70)',
              padding: '3px 6px',
              zIndex: 20,
              borderRadius: '0 0 0.5rem 0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* Alignment buttons */}
            <div style={{ display: 'flex', gap: 3 }}>
              {[
                { value: 'left',  Icon: AlignLeft,   label: 'Float left'  },
                { value: 'none',  Icon: AlignCenter,  label: 'No float'    },
                { value: 'right', Icon: AlignRight,   label: 'Float right' },
              ].map(({ value, Icon, label }) => (
                <button
                  key={value}
                  type="button"
                  title={label}
                  onClick={(e) => { e.stopPropagation(); applyAlign(value) }}
                  style={{
                    width: 22, height: 22,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 3, border: 'none',
                    background: align === value ? '#A67C52' : 'transparent',
                    color: '#fff', cursor: 'pointer', padding: 0,
                  }}
                >
                  <Icon size={13} />
                </button>
              ))}
            </div>

            {/* Current size + drag hint */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 4,
              fontSize: 10, color: 'rgba(255,255,255,0.7)',
            }}>
              <Move size={10} />
              {styleWidth || 'auto'}
            </div>
          </div>
        </>
      )}
    </NodeViewWrapper>
  )
}
